// CrossToolCoverageMapTeaser — v3.17.2
//
// Promoted from a tiny outline button into a proper teaser card so it has the
// same visual weight as its siblings inside the Bid Insights section (Self-Score
// Roll-up, Prior Proposals). Pulls a couple of cheap stats from the existing
// /coverage-map endpoint and renders an icon + 2 numbers + CTA arrow.
//
// Designed for the Go/No-Go dashboard Bid Insights section.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Map, ArrowUpRight, Layers, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CrossToolCoverageMapTeaser({ pursuitId, authToken }) {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [stats, setStats] = useState({ taskAreas: 0, gaps: 0, loading: true });

  useEffect(() => {
    if (!pursuitId || !authToken) return;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${authToken}` };
    const load = async () => {
      try {
        const [cmR, gapsR] = await Promise.all([
          fetch(`${API_URL}/api/pursuits/${pursuitId}/coverage-map`, { headers })
            .then(r => r.ok ? r.json() : null),
          fetch(`${API_URL}/api/pursuits/${pursuitId}/gaps`, { headers })
            .then(r => r.ok ? r.json() : []),
        ]);
        if (cancelled) return;
        const taskAreas = cmR?.task_areas?.length ?? cmR?.totals?.task_areas ?? 0;
        const gaps = Array.isArray(gapsR) ? gapsR.filter(g => g.status === 'GAP').length : 0;
        setStats({ taskAreas, gaps, loading: false });
      } catch (_) {
        if (!cancelled) setStats({ taskAreas: 0, gaps: 0, loading: false });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [pursuitId, authToken]);

  const handleOpen = () => navigate(`/${workspaceId}/pursuit/${pursuitId}/coverage-map`);

  return (
    <Card
      data-testid="cross-tool-coverage-map-teaser"
      className="border-cyan-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
      onClick={handleOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpen(); }}
      role="button"
      tabIndex={0}
    >
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center flex-shrink-0">
            <Map className="w-6 h-6 text-cyan-700" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 text-base mb-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Cross-Tool Coverage Map
            </h3>
            <p className="text-xs text-slate-500 leading-snug">
              See where each tool covers requirements and where gaps leak across the proposal stack.
            </p>
          </div>

          <div className="hidden sm:flex items-center gap-5 flex-shrink-0">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-slate-500 text-[10px] uppercase tracking-wider">
                <Layers className="w-3 h-3" /> Task Areas
              </div>
              <div className="text-xl font-bold text-cyan-700 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stats.loading ? '—' : stats.taskAreas}
              </div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-slate-500 text-[10px] uppercase tracking-wider">
                <AlertTriangle className="w-3 h-3" /> Gaps
              </div>
              <div
                className={`text-xl font-bold tabular-nums ${stats.gaps > 0 ? 'text-red-600' : 'text-slate-400'}`}
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {stats.loading ? '—' : stats.gaps}
              </div>
            </div>
          </div>

          <div className="flex-shrink-0">
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 group-hover:bg-cyan-100 transition-colors">
              Open Map
              <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
